import { $, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { trackDirtyForm } from '../lib/dirty-form.js';
import { fillForm } from '../lib/form.js';
import { findSelectMenu } from '../lib/select-menu.js';

/**
 * The Birthdays page.
 *
 * A single configuration, so `trackDirtyForm` directly rather than
 * `mountResourceEditor`: there is nothing to create and nothing to delete, and
 * the page has no identifier in its URL.
 */

interface Settings {
  channelId: string | null;
  message: string;
  hour: number;
  roleId: string | null;
  reminderDays: number;
  reminderChannelId: string | null;
}

interface PageData {
  settings: Settings;
  guildId: string;
}

export function initBirthdaysPage(): void {
  const form = $<HTMLFormElement>('[data-birthdays-form]');
  if (!form) return;

  const { settings, guildId } = pageData<PageData>();

  /*
   * The save bar is armed before any data is read: saving is this page's
   * essential function, and it must not depend on a component that can throw.
   */
  trackDirtyForm({
    form,
    successMessage: ct('birthdaysSaved', 'Birthdays saved.'),
    onSave: async () => {
      await api.put(`/api/birthdays?guildId=${guildId}`, {
        guildId,
        channelId: findSelectMenu(form, 'channelId')?.single ?? '',
        message: text(form, 'message'),
        hour: Number(text(form, 'hour')) || 0,
        roleId: findSelectMenu(form, 'roleId')?.single ?? '',
        reminderDays: Number(text(form, 'reminderDays')) || 0,
        reminderChannelId: findSelectMenu(form, 'reminderChannelId')?.single ?? '',
      });
    },
  });

  fillForm(form, settings as unknown as Record<string, unknown>);

  const channel = findSelectMenu(form, 'channelId');
  if (channel) channel.value = settings.channelId ? [settings.channelId] : [];

  const role = findSelectMenu(form, 'roleId');
  if (role) role.value = settings.roleId ? [settings.roleId] : [];

  const reminder = findSelectMenu(form, 'reminderChannelId');
  if (reminder) reminder.value = settings.reminderChannelId ? [settings.reminderChannelId] : [];
}

function text(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement
    ? field.value.trim()
    : '';
}
